import React from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, CheckCircle2, ShieldCheck, TrendingUp, Sparkles } from "lucide-react";
import NewsletterForm from "../components/NewsletterForm";

const Home = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const cur = lang === "en" ? "en" : "pe";

  const trustItems = t("trust.items", { returnObjects: true });
  const routeSteps = t("route.steps", { returnObjects: true });
  const features = t("features.items", { returnObjects: true });
  const cases = t("cases.items", { returnObjects: true });

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        style={{
          background: "linear-gradient(180deg,var(--pum-navy) 0%, var(--pum-navy-2) 100%)",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          className="pum-container"
          style={{
            display: "grid",
            gridTemplateColumns: "1.05fr 1fr",
            gap: "3rem",
            padding: "5rem 1.25rem 5.5rem",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.2em",
                  color: "var(--pum-emerald)",
                  fontWeight: 700,
                }}
              >
                <span style={{ width: 24, height: 1, background: "var(--pum-emerald)" }} /> {t("hero.eyebrow")}
              </span>
            </div>
            <h1
              data-testid="hero-title"
              style={{
                color: "#fff",
                fontSize: "clamp(2.15rem, 4.4vw, 3.6rem)",
                lineHeight: 1.1,
                marginBottom: "1.25rem",
                fontWeight: 900,
              }}
            >
              {t("hero.title")}
            </h1>
            <p
              style={{
                fontSize: "1.05rem",
                color: "#C6D2E5",
                marginBottom: "2rem",
                maxWidth: "540px",
                lineHeight: 1.6,
              }}
            >
              {t("hero.subtitle")}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.85rem" }}>
              <a
                href="#newsletter"
                className="pum-btn pum-btn--emerald"
                data-testid="hero-cta-primary"
              >
                {t("hero.ctaPrimary")} <ArrowRight size={16} />
              </a>
              <a
                href="#route"
                className="pum-btn"
                data-testid="hero-cta-secondary"
                style={{ background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.35)" }}
              >
                {t("hero.ctaSecondary")}
              </a>
            </div>

            <div
              style={{
                marginTop: "2.5rem",
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                color: "#8FA5C7",
                fontSize: "0.82rem",
              }}
            >
              <div style={{ height: 2, width: 48, background: "var(--pum-red)" }} />
              <span>{t("footer.flag")}</span>
            </div>
          </div>

          <div style={{ position: "relative" }}>
            <div
              style={{
                position: "relative",
                borderRadius: "6px",
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.1)",
                boxShadow: "0 30px 60px -20px rgba(0,0,0,0.6)",
              }}
            >
              <img
                src="/images/hero-business.jpg"
                alt={t("hero.imageAlt")}
                data-testid="hero-image"
                style={{ width: "100%", height: "460px", objectFit: "cover", display: "block" }}
              />
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  padding: "1rem 1.25rem",
                  background: "linear-gradient(180deg, transparent, rgba(11,31,58,0.85))",
                  fontSize: "0.78rem",
                  color: "#E4EBF7",
                  letterSpacing: "0.06em",
                }}
              >
                San Isidro · Lima · Perú
              </div>
            </div>
            <div
              style={{
                position: "absolute",
                right: "-14px",
                top: "-14px",
                background: "var(--pum-red)",
                color: "#fff",
                padding: "0.6rem 0.85rem",
                fontSize: "0.72rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontWeight: 700,
                borderRadius: "4px",
              }}
            >
              Bicentenario · 2026
            </div>
          </div>
        </div>

        <style>{`
          @media (max-width: 900px) {
            [data-testid="home-page"] section > div.pum-container {
              grid-template-columns: 1fr !important;
              padding: 3rem 1.25rem !important;
            }
            [data-testid="hero-image"] { height: 320px !important; }
          }
        `}</style>
      </section>

      {/* TRUST STRIP */}
      <section
        style={{ background: "var(--pum-mist)", padding: "3rem 0", borderBottom: "1px solid var(--pum-gray-200)" }}
        data-testid="trust-section"
      >
        <div className="pum-container">
          <div style={{ maxWidth: 760, marginBottom: "2.25rem" }}>
            <span className="pum-eyebrow">{t("trust.title")}</span>
            <p style={{ marginTop: "0.75rem", fontSize: "1.02rem", color: "var(--pum-gray-700)", lineHeight: 1.55 }}>
              {t("trust.subtitle")}
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "1rem",
            }}
            className="pum-trust-grid"
          >
            {trustItems.map((it, i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  border: "1px solid var(--pum-gray-200)",
                  borderTop: "3px solid var(--pum-emerald)",
                  padding: "1.5rem",
                  borderRadius: "4px",
                }}
                data-testid={`trust-item-${i}`}
              >
                <div style={{ fontFamily: "var(--font-serif)", fontSize: "1.9rem", color: "var(--pum-navy)", fontWeight: 900, marginBottom: "0.35rem" }}>
                  {it.value}
                </div>
                <div style={{ fontSize: "0.84rem", color: "var(--pum-gray-700)" }}>{it.label}</div>
              </div>
            ))}
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) {
            .pum-trust-grid { grid-template-columns: repeat(2, 1fr) !important; }
          }
        `}</style>
      </section>

      {/* ROUTE / METHODOLOGY */}
      <section id="route" className="pum-section" data-testid="route-section">
        <div
          className="pum-container"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1.1fr",
            gap: "3.5rem",
            alignItems: "center",
          }}
        >
          <div>
            <img
              src="/images/professional-woman.jpg"
              alt={t("route.imageAlt")}
              data-testid="route-image"
              style={{
                width: "100%",
                borderRadius: "6px",
                objectFit: "cover",
                aspectRatio: "4/5",
                filter: "saturate(1.05)",
              }}
            />
          </div>
          <div>
            <span className="pum-eyebrow">{t("route.eyebrow")}</span>
            <h2 style={{ fontSize: "clamp(1.85rem, 3vw, 2.6rem)", marginTop: "1rem", marginBottom: "1rem" }}>
              {t("route.title")}
            </h2>
            <p style={{ color: "var(--pum-gray-700)", fontSize: "1rem", lineHeight: 1.6, marginBottom: "2rem" }}>
              {t("route.subtitle")}
            </p>
            <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "1rem" }}>
              {routeSteps.map((s, i) => (
                <li
                  key={i}
                  data-testid={`route-step-${i}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    gap: "1.1rem",
                    padding: "1.15rem",
                    background: "#fff",
                    border: "1px solid var(--pum-gray-200)",
                    borderLeft: "3px solid var(--pum-navy)",
                    borderRadius: "4px",
                  }}
                >
                  <div style={{ fontFamily: "var(--font-serif)", fontSize: "1.4rem", fontWeight: 900, color: "var(--pum-emerald)" }}>
                    {s.number}
                  </div>
                  <div>
                    <h3 style={{ fontSize: "1.05rem", marginBottom: "0.35rem" }}>{s.title}</h3>
                    <p style={{ color: "var(--pum-gray-700)", fontSize: "0.9rem", lineHeight: 1.55 }}>{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) {
            [data-testid="route-section"] > div.pum-container {
              grid-template-columns: 1fr !important;
              gap: 2rem !important;
            }
            [data-testid="route-image"] { aspect-ratio: 4/3 !important; }
          }
        `}</style>
      </section>

      {/* FEATURES */}
      <section
        className="pum-section"
        style={{ background: "var(--pum-mist)", borderTop: "1px solid var(--pum-gray-200)", borderBottom: "1px solid var(--pum-gray-200)" }}
        data-testid="features-section"
      >
        <div className="pum-container">
          <div style={{ maxWidth: 800, marginBottom: "2.5rem" }}>
            <span className="pum-eyebrow">{t("features.eyebrow")}</span>
            <h2 style={{ marginTop: "0.75rem", fontSize: "clamp(1.75rem, 3vw, 2.4rem)", marginBottom: "0.9rem" }}>
              {t("features.title")}
            </h2>
            <p style={{ color: "var(--pum-gray-700)", fontSize: "1rem", lineHeight: 1.6 }}>{t("features.subtitle")}</p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: "1.25rem",
            }}
            className="pum-features-grid"
          >
            {features.map((f, i) => (
              <div
                key={i}
                data-testid={`feature-${i}`}
                style={{
                  background: "#fff",
                  padding: "1.75rem",
                  borderRadius: "6px",
                  border: "1px solid var(--pum-gray-200)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.85rem",
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: "4px",
                    background: "var(--pum-emerald-soft)",
                    color: "var(--pum-emerald)",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  {i % 3 === 0 ? <TrendingUp size={20} /> : i % 3 === 1 ? <ShieldCheck size={20} /> : <Sparkles size={20} />}
                </div>
                <h3 style={{ fontSize: "1.05rem" }}>{f.title}</h3>
                <p style={{ color: "var(--pum-gray-700)", fontSize: "0.9rem", lineHeight: 1.55 }}>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) { .pum-features-grid { grid-template-columns: 1fr 1fr !important; } }
          @media (max-width: 600px) { .pum-features-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </section>

      {/* CASES */}
      <section className="pum-section" data-testid="cases-section">
        <div className="pum-container">
          <div style={{ marginBottom: "2.5rem", maxWidth: 720 }}>
            <span className="pum-eyebrow">{t("cases.eyebrow")}</span>
            <h2 style={{ marginTop: "0.75rem", fontSize: "clamp(1.75rem, 3vw, 2.4rem)" }}>{t("cases.title")}</h2>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1.25rem" }}
            className="pum-cases-grid"
          >
            {cases.map((c, i) => (
              <figure
                key={i}
                data-testid={`case-${i}`}
                style={{
                  margin: 0,
                  padding: "2rem",
                  border: "1px solid var(--pum-gray-200)",
                  borderTop: "3px solid var(--pum-emerald)",
                  borderRadius: "6px",
                  background: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.25rem",
                }}
              >
                <blockquote style={{ margin: 0, fontFamily: "var(--font-serif)", fontSize: "1.05rem", lineHeight: 1.6, color: "var(--pum-navy)" }}>
                  “{c.quote}”
                </blockquote>
                <figcaption style={{ borderTop: "1px solid var(--pum-gray-200)", paddingTop: "1rem" }}>
                  <div style={{ fontWeight: 700, color: "var(--pum-navy)" }}>{c.name}</div>
                  <div style={{ fontSize: "0.82rem", color: "var(--pum-gray-500)" }}>{c.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) { .pum-cases-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </section>

      {/* CTA + Newsletter */}
      <section
        id="newsletter"
        style={{ background: "var(--pum-navy)", color: "#fff", padding: "5.5rem 0" }}
        data-testid="newsletter-section"
      >
        <div
          className="pum-container"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "3rem",
            alignItems: "center",
          }}
        >
          <div>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.72rem",
                textTransform: "uppercase",
                letterSpacing: "0.2em",
                color: "var(--pum-emerald)",
                fontWeight: 700,
              }}
            >
              <span style={{ width: 24, height: 1, background: "var(--pum-emerald)" }} /> {t("cta.eyebrow")}
            </span>
            <h2 style={{ color: "#fff", fontSize: "clamp(1.85rem, 3.2vw, 2.7rem)", marginTop: "1rem", marginBottom: "1rem" }}>
              {t("cta.title")}
            </h2>
            <p style={{ color: "#C6D2E5", fontSize: "1rem", lineHeight: 1.6, marginBottom: "1.75rem", maxWidth: 520 }}>
              {t("cta.subtitle")}
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {[0, 1, 2].map((i) => (
                <li key={i} style={{ display: "flex", gap: "0.65rem", alignItems: "flex-start" }}>
                  <CheckCircle2 size={18} style={{ marginTop: 2, color: "var(--pum-emerald)" }} />
                  <span style={{ color: "#E4EBF7", fontSize: "0.92rem" }}>
                    {t(`features.items.${i}.title`)}
                  </span>
                </li>
              ))}
            </ul>

            <div style={{ marginTop: "2rem", fontSize: "0.8rem", color: "#8FA5C7", maxWidth: 520 }}>
              {t("cta.note")}{" "}
              <Link
                to={`/${cur}/privacy`}
                data-testid="cta-privacy-link"
                style={{ color: "var(--pum-emerald)", textDecoration: "underline" }}
              >
                {t("nav.privacy")}
              </Link>
            </div>
          </div>

          <div>
            <NewsletterForm />
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) {
            [data-testid="newsletter-section"] > div.pum-container {
              grid-template-columns: 1fr !important;
              gap: 2rem !important;
            }
          }
        `}</style>
      </section>
    </div>
  );
};

export default Home;
